// Initialize form
import { io } from "socket.io-client";
import * as Countries from "./countries";
import * as States from "./states";
import * as Languages from "./languages";
import * as DynamicContentLoader from "./dynamicContentLoader";
import { translateText } from './translator';
import {
  initSignaturePad,
  getSignatureDataUrl,
  isSignatureEmpty,
  isSignatureValid,
} from "./signaturePadModule";
import { applyProgramTrackPrefill } from './zahsLastInterest.js';
import { ur } from "intl-tel-input/i18n";

const newApplicationDiv = document.getElementById("application_form_new");

let selectTranslation = "Select";

// Identify what each step contains without assuming fixed step numbers
function getStepType(stepNumber) {
  const stepEl = document.querySelector(`.step-${stepNumber}`);
  if (!stepEl) return null;
  if(!newApplicationDiv){
    if (stepEl.querySelector('#first_name') || stepEl.querySelector('#new-password')) return 'profile';
  }else{
    if (stepEl.querySelector('#first_name')) return 'profile';
    if (stepEl.querySelector('#new-password')) return 'password';
  }
  if (stepEl.querySelector('#street') || stepEl.querySelector('#country')) return 'address';
  if (stepEl.querySelector('#program_interest')) return 'credits';
  if (stepEl.querySelector('#acknowledge') || stepEl.querySelector('#signature-pad')) return 'enrollment';
  return null;
}

// Configuration
const stepCircles = document.querySelectorAll(".step-circle");
const config = {
  totalSteps: stepCircles.length || 4 // fallback to 4
};

let relationshipOptions = {};
let affiliations = [];
let programOptions = [];
let trackOptions = {};

let presenceSocket = null;
let heartbeatInterval = null;

let initialAffiliationFromServer = null;

// State management
const state = {
  currentStep: 1,
  guardian2Added: false
};

let draftApp = {
  name: null,
  token: null,
};
let stepTransitionInFlight = false;

let zahsApplicationRedirectCheck = null;
let zahsApplicationRedirectCheckToken = "";
let zahsApplicationPageShowBound = false;

function getCurrentApplicationToken() {
  const knownToken = String(draftApp.token || window.token || "").trim();
  if (knownToken) {
    return knownToken;
  }

  const params = new URLSearchParams(window.location.search);
  return String(params.get("token") || "").trim();
}

function syncDraftTokenToUrl() {
  const token = getCurrentApplicationToken();
  if (!token || !window.history?.replaceState) {
    return;
  }

  try {
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.get("token") === token) {
      window.token = token;
      return;
    }

    currentUrl.searchParams.set("token", token);
    window.history.replaceState(window.history.state, "", currentUrl.toString());
    window.token = token;
  } catch (error) {
    console.debug("Unable to sync ZAHS application token into the URL:", error);
  }
}

function updateDraftAppIdentity(name = null, token = null) {
  const normalizedName = typeof name === "string" ? name.trim() : "";
  if (normalizedName) {
    draftApp.name = normalizedName;
  }

  const normalizedToken = typeof token === "string" ? token.trim() : "";
  if (normalizedToken) {
    draftApp.token = normalizedToken;
    syncDraftTokenToUrl();
  }
}

const CAP_TOKEN_TTL_MS = 110000;
let zahsCapClient = null;
let zahsCapInFlight = null;
let zahsCapSolvedAt = 0;

function normalizeCapToken(value) {
  if (typeof value === "string") {
    return value.trim();
  }
  if (value && typeof value === "object") {
    if (typeof value.detail?.token === "string") {
      return value.detail.token.trim();
    }
    if (typeof value.token === "string") {
      return value.token.trim();
    }
  }
  return "";
}

function getZahsCapApiEndpoint() {
  const endpointInput = document.getElementById("zahs-cap-api-endpoint");
  return endpointInput ? String(endpointInput.value || "").trim() : "";
}

function setZahsCapToken(value) {
  const tokenInput = document.getElementById("zahs-cap-token");
  const normalized = normalizeCapToken(value);
  if (tokenInput) {
    tokenInput.value = normalized;
  }
  zahsCapSolvedAt = normalized ? Date.now() : 0;
}

function getZahsCapToken() {
  const tokenInput = document.getElementById("zahs-cap-token");
  return tokenInput ? String(tokenInput.value || "").trim() : "";
}

function hasFreshZahsCapToken() {
  const token = getZahsCapToken();
  if (!token) {
    return false;
  }
  return Date.now() - zahsCapSolvedAt < CAP_TOKEN_TTL_MS;
}

function isZahsCapEnabled() {
  return Boolean(getZahsCapApiEndpoint());
}

function getOrCreateZahsCapClient() {
  const apiEndpoint = getZahsCapApiEndpoint();
  if (!apiEndpoint || !window.Cap) {
    return null;
  }

  if (!zahsCapClient || zahsCapClient.__apiEndpoint !== apiEndpoint) {
    zahsCapClient = new window.Cap({ apiEndpoint });
    zahsCapClient.__apiEndpoint = apiEndpoint;
  }
  return zahsCapClient;
}

async function solveZahsCapToken(force = false) {
  if (!isZahsCapEnabled()) {
    return "";
  }

  if (!force && hasFreshZahsCapToken()) {
    return getZahsCapToken();
  }

  if (zahsCapInFlight) {
    return zahsCapInFlight;
  }

  zahsCapInFlight = (async () => {
    const capClient = getOrCreateZahsCapClient();
    if (!capClient) {
      throw new Error("CAP client is not available");
    }

    const solveResult = await capClient.solve();
    const token = normalizeCapToken(solveResult?.token || capClient.token);
    if (!token) {
      throw new Error("CAP solve did not return a token");
    }

    setZahsCapToken(token);
    return token;
  })();

  try {
    return await zahsCapInFlight;
  } finally {
    zahsCapInFlight = null;
  }
}

function primeZahsCapToken() {
  if (!isZahsCapEnabled() || hasFreshZahsCapToken() || zahsCapInFlight) {
    return;
  }
  solveZahsCapToken(false).catch((err) => {
    console.debug("ZAHS CAP pre-solve failed:", err);
  });
}

async function getFreshZahsCapHeaders(extraHeaders = {}) {
  const headers = { ...extraHeaders };
  if (!isZahsCapEnabled()) {
    return headers;
  }

  const token = await solveZahsCapToken(true);
  headers["X-Cap-Token"] = token;
  return headers;
}

async function buildZahsProtectedHeaders(extraHeaders = {}) {
  return getFreshZahsCapHeaders({
    "X-Frappe-CSRF-Token": frappe.csrf_token,
    ...extraHeaders,
  });
}

function stripHtmlForAlert(message) {
  const container = document.createElement("div");
  container.innerHTML = String(message || "");
  return (container.textContent || container.innerText || "").trim();
}

function extractFrappeErrorMessage(payload) {
  const directMessage = payload?.message;
  if (typeof directMessage === "string" && directMessage.trim()) {
    return directMessage.trim();
  }
  if (directMessage && typeof directMessage === "object") {
    if (typeof directMessage.message === "string" && directMessage.message.trim()) {
      return directMessage.message.trim();
    }
    if (typeof directMessage._error_message === "string" && directMessage._error_message.trim()) {
      return directMessage._error_message.trim();
    }
  }

  if (typeof payload?._error_message === "string" && payload._error_message.trim()) {
    return payload._error_message.trim();
  }

  if (typeof payload?._server_messages === "string" && payload._server_messages.trim()) {
    try {
      const serverMessages = JSON.parse(payload._server_messages);
      for (const entry of serverMessages) {
        if (typeof entry === "string") {
          try {
            const parsedEntry = JSON.parse(entry);
            if (typeof parsedEntry?.message === "string" && parsedEntry.message.trim()) {
              return parsedEntry.message.trim();
            }
          } catch (error) {
            if (entry.trim()) {
              return entry.trim();
            }
          }
        } else if (typeof entry?.message === "string" && entry.message.trim()) {
          return entry.message.trim();
        }
      }
    } catch (error) {
      return payload._server_messages.trim();
    }
  }

  return "";
}

async function showAlertOrFallback(message, options = {}) {
  if (typeof window.showAlert === "function") {
    await window.showAlert(message, options);
    return;
  }

  const fallbackMessage = stripHtmlForAlert(message)
    || await translateText("Something went wrong. Please try again.");
  window.alert(fallbackMessage);
}

async function maybeRedirectRecoverableApplication(source = "load") {
  const token = getCurrentApplicationToken();
  if (!token) {
    return false;
  }

  if (
    zahsApplicationRedirectCheck
    && zahsApplicationRedirectCheckToken === token
  ) {
    return zahsApplicationRedirectCheck;
  }

  zahsApplicationRedirectCheckToken = token;
  zahsApplicationRedirectCheck = (async () => {
    try {
      const response = await fetch(
        "/api/method/"
        + "zoni_edu.zoni_lms.doctype.zahs_application.zahs_application."
        + `get_zahs_application_redirect_state?token=${encodeURIComponent(token)}`,
        {
          headers: {
            Accept: "application/json",
          },
          credentials: "include",
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          extractFrappeErrorMessage(payload)
          || "Failed to verify application redirect state."
        );
      }

      const redirectState = payload.message || {};
      updateDraftAppIdentity(redirectState.name || null, redirectState.token || token);

      const redirectUrl = String(redirectState.redirect_url || "").trim();
      if (redirectState.should_redirect && redirectUrl) {
        window.location.replace(redirectUrl);
        return true;
      }
    } catch (error) {
      console.debug(
        `ZAHS application redirect check failed during ${source}:`,
        error
      );
    }

    return false;
  })();

  try {
    return await zahsApplicationRedirectCheck;
  } finally {
    zahsApplicationRedirectCheck = null;
    zahsApplicationRedirectCheckToken = "";
  }
}

function bindRecoverableApplicationRedirectCheck() {
  if (zahsApplicationPageShowBound) {
    return;
  }

  zahsApplicationPageShowBound = true;
  window.addEventListener("pageshow", () => {
    maybeRedirectRecoverableApplication("pageshow");
  });
}

function setStepNavigationInteractionDisabled(disabled) {
  document.querySelectorAll(".next-step").forEach((button) => {
    button.disabled = disabled;
  });
  document.querySelectorAll(".step-circle").forEach((circle) => {
    circle.style.pointerEvents = disabled ? "none" : "";
  });
}

async function uploadZahsApplicationAttachment(fieldname, file, filename = null) {
  if (!draftApp.token) {
    throw new Error(await translateText("Application session expired. Please refresh and try again."));
  }

  const formData = new FormData();
  formData.append("token", draftApp.token);
  formData.append("fieldname", fieldname);
  if (filename) {
    formData.append("file", file, filename);
  } else {
    formData.append("file", file);
  }

  const response = await fetch(
    "/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.attach_zahs_application_file",
    {
      method: "POST",
      headers: await buildZahsProtectedHeaders(),
      body: formData,
    }
  );
  const payload = await response.json().catch(() => ({}));
  const message = payload.message || {};
  if (!response.ok || message.ok === false) {
    const fallbackError = fieldname === "signature_data"
      ? await translateText("Failed to upload the signature.")
      : await translateText("Failed to upload the transcript.");
    throw new Error(extractFrappeErrorMessage(payload) || fallbackError);
  }

  updateDraftAppIdentity(message.name || null, message.token || draftApp.token);
  return message;
}

// ─────────────────────────────────────────────────────────────
// Password Step: dynamic, accessible validation w/ backend check
// ─────────────────────────────────────────────────────────────
let passwordState = {
  meetsLocal: false,
  meetsBackend: false,
  lastBackendValue: '',
  backendInFlight: false,
};

// DOM Elements
const elements = {
  progressBar: document.querySelector(".progress-bar-application"),
  studentNameSpan: document.getElementById("student_name_span"),
  formContainer: document.getElementById("form-container"),
  passwordInput: document.getElementById("password"),
  lengthReq: document.getElementById("lengthReq"),
  upperReq: document.getElementById("upperReq"),
  numberReq: document.getElementById("numberReq"),
  form: document.getElementById("multi-step-form"),
};

export async function initApplicationForm() {
  selectTranslation = await translateText("Select");
  bindRecoverableApplicationRedirectCheck();
  setupEventListeners();
  updateProgressBar();
  setDOBMax();
  setupCreditsStepBehavior();
  setupDragAndDropUpload();
  hideAllSteps();
  prefillFromURL();
  updateDraftAppIdentity(null, getCurrentApplicationToken());
  if (await maybeRedirectRecoverableApplication("init")) {
    return;
  }
  checkIfParentFormHasErrors();
  if (document.getElementById('new-password')) {
    initPasswordStep();
  }

  const dobInput = document.getElementById("date_of_birth");
  const guardian1Block = document.getElementById("guardian-section");
  const guardian2Toggle = document.getElementById("guardian2-toggle-container");
  const addGuardian2Btn = document.getElementById("add-guardian2-btn");
  const guardian2Block = document.getElementById("guardian2-section");
  const firstNameInput = document.getElementById("first_name");

  if (!dobInput || !guardian1Block || !guardian2Toggle || !addGuardian2Btn || !guardian2Block) {
    return;
  }

  if (firstNameInput) {
    updateStudentNameDisplay();
    firstNameInput.addEventListener("input", updateStudentNameDisplay);
  }

  if (document.querySelector("#student_phone_number")) {
    itiStudent = DynamicContentLoader.loadAndInitializeIntlTelApplicationInput("#student_phone_number");
  }
  if (document.querySelector("#guardian_phone_number")) {
    itiGuardian = DynamicContentLoader.loadAndInitializeIntlTelGuardianInput("#guardian_phone_number");
  }
  if (document.querySelector("#guardian2_phone_number")) {
    itiGuardian2 = DynamicContentLoader.loadAndInitializeIntlTelGuardian2Input("#guardian2_phone_number");
  }

  step1 = document.querySelector(".step-1");
  if (step1) {
    document.querySelector(".step-1").style.display = "block";
  }

  resumeFromSavedStepIfNeeded();

  // Hide by default (new applications)
  toggleGuardian(false);
  await toggleGuardian2(false);

  // If we're resuming an application, restore guardians based on saved data
  if (isResumeMode()) {
    const dobVal = dobInput.value;
    if (dobVal) {
      const age = calculateAge(new Date(dobVal));
      const isMinor = age < 18;

      // Show guardian 1 if minor
      toggleGuardian(isMinor);

      // Show/hide the "Add another Parent/Guardian" button
      guardian2Toggle.style.display = isMinor ? "block" : "none";

      // If there is already guardian2 data saved, show guardian #2 section
      const guardian2HasData = [
        "guardian2_first_name",
        "guardian2_last_name",
        "guardian2_email",
        "guardian2_phone_number",
        "guardian2_rel_category",
        "guardian2_relationship",
      ].some((id) => {
        const el = document.getElementById(id);
        return el && el.value && el.value.trim().length;
      });

      if (isMinor && guardian2HasData) {
        await toggleGuardian2(true);
      }
    }
  }

  // ── load guardian/parent relationships from the server
  relationshipOptions = await fetchRelationshipOptions();
  populateRelationshipCategories();
  wireUpRelationshipHandlers();

  // whenever dob changes, show/hide guardian #1 and toggle button
  dobInput.addEventListener("change", e => {
    const age = calculateAge(new Date(e.target.value));
    const isMinor = age < 18;

    toggleGuardian(isMinor);
    guardian2Toggle.style.display = isMinor ? "block" : "none";

    // clear out any second-guardian state if no longer a minor
    if (!isMinor && state.guardian2Added) {
      toggleGuardian2(false);
    }
  });

  // click “Add another Parent/Guardian” to show/hide #2
  addGuardian2Btn.addEventListener("click", () => {
    toggleGuardian2(!state.guardian2Added);
  });

  function toggleGuardian(show) {
    guardian1Block.style.display = show ? "block" : "none";
    // update required attributes
    const fields = [
      "guardian_first_name",
      "guardian_last_name",
      "guardian_email",
      "guardian_phone_number",
      "guardian_rel_category",
      "guardian_relationship"
    ];
    fields.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (show) el.setAttribute("required", "");
      else     el.removeAttribute("required");
    });
  }

  async function toggleGuardian2(show) {
    state.guardian2Added = show;
    guardian2Block.style.display = show ? "block" : "none";
    addGuardian2Btn.innerHTML  = show
      ? `<i class="fa-solid fa-user-minus me-1"></i> ${await translateText('Remove Parent/Guardian')}`
      : `<i class="fa-solid fa-user-plus me-1"></i> ${await translateText('Add another Parent/Guardian')}`;

    // update required attributes
    const fields = [
      "guardian2_first_name",
      "guardian2_last_name",
      "guardian2_email",
      "guardian2_phone_number",
      "guardian2_rel_category",
      "guardian2_relationship"
    ];
    fields.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      if (show) el.setAttribute("required", "");
      else     el.removeAttribute("required");
    });
  }

  // populate country selects
  const countriesIds = [
    "country_of_birth",
    "country_of_citizenship",
    "country"
  ];
  Countries.populateCountrySelects(countriesIds);
  Countries.initCountrySelect2(countriesIds);

  // populate state selects
  const stateIds = [
    "state"
  ];
  await States.loadStates();
  States.populateStateSelects(stateIds);
  States.initStateSelect2(stateIds);
  setupCountryStateLinkage();
  await prefillAddressFromIP();

  affiliations = await fetchAffiliations();
  buildAffiliationMaps();
  populateProgramSelect();
  wireUpProgramTrackHandlers();

  const shouldPrefillFromDraft =
  isResumeMode() && !!initialAffiliationFromServer;

  if (shouldPrefillFromDraft) {
    // We have a draft ZAHS Application loaded in the context
    // → restore program & track from its affiliation
    prefillDraftCreditsFromAffiliation();
  } else {
    // Normal behavior: CRM / last-interest prefill
    applyProgramTrackPrefill();
  }

  await Languages.loadLanguages();
  Languages.populateLanguageSelects(["primary_language"]);
  Languages.initLanguageSelect2(["primary_language"]);
  initEnrollmentWhenVisible();
  initSubmitButton();
  prefillTranscriptUploadRequired();
}

function prefillTranscriptUploadRequired(){
  const select_grade_lvl = document.getElementById("grade_level");
  const select_credit_transfer=document.getElementById("transfer_credits");

  if(!!!select_grade_lvl || !!!select_credit_transfer){
    console.warn("Expected elements not found in dom.")
    return;
  }

  select_grade_lvl.addEventListener("change", function (event) {
    const value=event.target.value
    const grade= parseInt(value)
    if(!!!value){
      select_credit_transfer.value = "";
    }
    else if([10,11,12].includes(grade)){
      select_credit_transfer.value = "yes";
    }
    else{
        select_credit_transfer.value = "";
    }
    select_credit_transfer.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

// Check parent form for errors
function checkIfParentFormHasErrors() {
  const parentEl = window.parent.document.getElementById('ApplicationInfo1_lbErr');
  if (!parentEl) return;
  if (parentEl) {
    const errEl = parentEl.querySelector('.errormsg');
    if (errEl) {
      const errorContainer = document.getElementById('errorContainer');
      const rowErrorContainer = document.getElementById('errorRow');
      if (errorContainer && rowErrorContainer) {
        errorContainer.innerHTML = errEl.innerHTML;
        rowErrorContainer.style.display = 'block';
      }
    }
  }
}

// Prefill Form
function prefillFromURL() {
  // pick the “right” query string (self vs parent)
  const search = (window.self !== window.top && window.parent.location)
    ? window.parent.location.search
    : window.location.search;

  const params = new URLSearchParams(search);
  for (let [key, value] of params.entries()) {
    const el = document.getElementById(key);
    if (!el) continue;

    // If this select is enhanced by Select2:
    if (window.$ && $(el).data('select2')) {
      $(el).val(value).trigger('change');
    } else {
      el.value = value;
      // notify any attached listeners
      el.dispatchEvent(new Event('input',  { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

// Event Listeners
export function setupEventListeners() {
  // Step navigation circles
  document.querySelectorAll(".step-circle").forEach((circle) => {
    circle.addEventListener("click", async function () {
      if (stepTransitionInFlight) {
        return;
      }

      const targetStep = parseInt(this.getAttribute("data-step"), 10);

      // If moving forward, validate current step based on what it is
      if (targetStep > state.currentStep) {
        const currentType = getStepType(state.currentStep);
        let valid = true;
        switch (currentType) {
          case 'profile':
            valid = await validateProfileStep();
            break;
          case 'address':
            valid = validateAddress();
            break;
          case 'password':
            // let the password step’s own button guard progression;
            // but if you want to block circle jumping:
            valid = true; // or compute from passwordState if desired
            break;
          case 'credits':
            valid = validateCreditsStep();
            break;
          default:
            break;
        }
        if (!valid) return;
      }
      goToStep(targetStep);
    });
  });

  // Main form next step button
  document.querySelectorAll(".next-step").forEach((button) => {
    button.addEventListener("click", async function () {
      if (stepTransitionInFlight) {
        return;
      }

      const nextStep = parseInt(this.getAttribute("data-next"));
      await handleNextStep(nextStep);
    });
  });

  // Assistance toggle
  needs_assistanceToggle = document.getElementById("needs_assistance");
  if (needs_assistanceToggle) {
    needs_assistanceToggle.addEventListener("change", function () {
      toggleAssistanceDetails(this.value === "yes");
    });
  }

  // Password validation
  if (elements.passwordInput) {
    elements.passwordInput.addEventListener("input", validatePassword);
  }
}

// Form Functions
function getCurrentStepType() {
  return getStepType(state.currentStep) || "";
}

function buildHeartbeatQuery(reason = "ping") {
  if (!draftApp.token) return null;

  const stepType = getCurrentStepType();
  const params = new URLSearchParams({
    token: draftApp.token,
    reason: reason,
    step: stepType,
    step_index: String(state.currentStep || 0),
  });

  return params.toString();
}

/**
 * Fire-and-forget HTTP GET heartbeat.
 * Used both by the interval and by WebSocket callbacks.
 */
function sendHeartbeat(reason = "ping") {
  const qs = buildHeartbeatQuery(reason);
  if (!qs) return;

  fetch(
    `/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.heartbeat?${qs}`,
    {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "X-Frappe-CSRF-Token": frappe.csrf_token,
      },
      credentials: "include",
    }
  ).catch(() => {
    // best-effort; ignore network errors
  });
}

/**
 * Backup heartbeat loop (HTTP GET every 30s while tab visible).
 */
function startHttpHeartbeatLoop() {
  if (heartbeatInterval) return; // already running

  heartbeatInterval = setInterval(() => {
    // Avoid spamming while totally hidden (optional, but nice)
    if (document.visibilityState === "hidden") return;
    sendHeartbeat("interval");
  }, 30_000); // 30 seconds
}

/**
 * Initialize presence tracking:
 * - WebSocket connection to detect disconnects.
 * - HTTP fallback heartbeat loop.
 * - beforeunload beacon when user closes/leaves the page.
 */
function initPresenceTracking() {
  // 1) Socket.io: connect to the same namespace pattern you use elsewhere
  const siteName = window.frappeSiteName || window.location.hostname;
  const namespace = "/" + siteName;

  presenceSocket = io(window.location.origin + namespace, {
    path: "/socket.io",
    withCredentials: true,
    transports: ["websocket", "polling"],
  });

  presenceSocket.on("connect", () => {
    // mark user as online and active
    sendHeartbeat("ws_connect");
  });

  presenceSocket.on("disconnect", (reason) => {
    // This normally fires before the page is completely gone,
    // so we can still tell the backend.
    sendHeartbeat(`ws_disconnect:${reason || "unknown"}`);
  });

  // 2) HTTP backup heartbeat
  startHttpHeartbeatLoop();

  // 3) beforeunload → explicit "user left this page" signal
  window.addEventListener("beforeunload", () => {
    if (!draftApp.token) return;

    const qs = buildHeartbeatQuery("beforeunload");
    if (!qs) return;

    const url = `/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.heartbeat?${qs}`;

    // sendBeacon is more reliable than fetch during unload
    try {
      navigator.sendBeacon(url);
    } catch (err) {
      // If sendBeacon fails, we just accept that we might miss this one.
      console.error("sendBeacon heartbeat failed:", err);
    }
  });
}


async function autosaveCurrentStep(stepType) {
  try {
    const formData = await collectFormData();
    const headers = await buildZahsProtectedHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    });

    // Ensure we have a token (only once)
    if (!draftApp.token) {
      updateDraftAppIdentity(null, generateUUID());
    }

    const res = await fetch(
      "/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.autosave_zahs_application_step",
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          step: stepType,
          step_index: state.currentStep,
          token: draftApp.token,
          data: JSON.stringify(formData)
        }),
      }
    );

    const out = await res.json();
    if (res.ok && out.message) {
      if (out.message.status === "existing_application_handled") {
        const recoveryTitle = await getExistingApplicationResponseTitle(
          out.message
        );
        const recoveryMessage = out.message.message || await translateText(
          "A previous ZAHS application already exists for that email."
        );
        const recoveryHtml = buildExistingApplicationMessageHtml(
          recoveryMessage,
          out.message
        );
        await showAlertOrFallback(recoveryHtml, { title: recoveryTitle });
        return false;
      }

      updateDraftAppIdentity(out.message.name || null, out.message.token || null);
      if (!presenceSocket) {
        initPresenceTracking();
      }
      return true;
    } else {
      console.error("Autosave error:", out);
      await showAlertOrFallback(
        extractFrappeErrorMessage(out)
        || await translateText("Unable to save your progress. Please try again."),
        {
          title: await translateText("Unable to continue"),
        }
      );
    }
  } catch (err) {
    console.error("Autosave failed:", err);
    const message = err instanceof Error && err.message
      ? err.message
      : await translateText("Unable to save your progress. Please try again.");
    await showAlertOrFallback(message, {
      title: await translateText("Unable to continue"),
    });
  }

  return false;
}

function isResumeMode() {
  return String(window.zahsApplicationResumeMode || "").toLowerCase() === "true";
}

function resumeFromSavedStepIfNeeded() {
  const resumeMode = isResumeMode();
  if (!resumeMode) return;

  let token = String(window.token || "").trim();
  if (!token || token.length === 0) return;
  updateDraftAppIdentity(null, token);

  if (!presenceSocket) {
      initPresenceTracking();
  }

  let savedStep = parseInt(window.step || '0', 10);
  if (!Number.isFinite(savedStep) || savedStep < 1) {
    savedStep = 1;
  }

  // Don’t go past the total steps
  if (savedStep > config.totalSteps) {
    savedStep = config.totalSteps;
  }

  // If step is 1, we’re already there, nothing to do
  if (savedStep === 1) return;

  // We know step 1 is visible at this point
  state.currentStep = 1;
  goToStep(savedStep);
}


export async function handleNextStep(nextStep) {
  if (stepTransitionInFlight) {
    return;
  }

  stepTransitionInFlight = true;
  setStepNavigationInteractionDisabled(true);

  try {
    // if going backwards, just jump
    if (nextStep < state.currentStep) {
      goToStep(nextStep);
      return;
    }

    const currentType = getStepType(state.currentStep);
    let valid = true;

    switch (currentType) {
      case 'profile':
        // Password is now collected after payment, not in profile step
        valid = await validateProfileStep();
        break;
      case 'address':
        valid = validateAddress();
        break;
      case 'password':
        // Password step is no longer used in the application form
        valid = true;
        break;
      case 'credits':
        valid = validateCreditsStep();
        break;
      default:
        valid = true;
    }

    if (!valid) {
      const form = document.getElementById("multi-step-form");
      form?.classList.add("was-validated");
      document
        .getElementById("form-container")
        ?.querySelector(".is-invalid")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (currentType) {
      const autosaveOk = await autosaveCurrentStep(currentType);
      if (!autosaveOk) {
        return;
      }
    }

    goToStep(nextStep);
  } finally {
    stepTransitionInFlight = false;
    setStepNavigationInteractionDisabled(false);
  }
}

// Treat any "en", "en-US", "en-GB", etc. as English (case-insensitive)
function isEnglishCode(code = "") {
  return typeof code === "string" && code.trim().toLowerCase().startsWith("en");
}

/**
 * If ESOL (program === "5") AND primary_language is English,
 * mark program invalid and show a specific message.
 * Returns true if valid, false if blocked by this rule.
 */
function enforceESOLLanguageRule({ programSelect, langSelect }) {
  if (!programSelect || !langSelect) return true;

  const baseErr = document.getElementById("application-program-error");
  const esolErr = document.getElementById("esol-language-error");
  const esolValue = "5";

  if (!baseErr || !esolErr) return true;

  // reset state
  programSelect.setCustomValidity("");
  programSelect.classList.remove("is-invalid");
  baseErr?.classList.remove("d-none");
  esolErr?.classList.add("d-none");

  if (!langSelect) return true;

  const isESOL = programSelect.value === esolValue;
  const isEnglish = isEnglishCode(langSelect.value);

  if (isESOL && isEnglish) {
    programSelect.setCustomValidity(" ");
    programSelect.classList.add("is-invalid");
    baseErr?.classList.add("d-none");
    esolErr?.classList.remove("d-none");
    return false;
  }

  return true;
}

export function validateCreditsStep() {
  const program = document.getElementById("program_interest").value;
  const track   = document.getElementById("track_interest").value;
  const transfer_credits     = document.getElementById("transfer_credits").value;
  const form = document.getElementById("multi-step-form");
  const placementYes      = document.getElementById("take_esol_placement_exam");
  const placementNo       = document.getElementById("skip_esol_placement_exam");
  const placementContainer = document.getElementById("placement-test-container");
  const placementError     = placementContainer
    ? placementContainer.querySelector(".invalid-feedback")
    : null;

  form.classList.add("was-validated");
  const fields = [
    "program_interest", "grade_level",
    "transfer_credits", "needs_assistance"
  ];

  let isValid = true;

  fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el || !el.value.trim() || !el.value.trim().length) {
      isValid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });

  if (program === "5") {
    const lang = document.getElementById("primary_language");
    if (!lang || !lang.value.trim()) {
      isValid = false;
      if (lang) lang.classList.add("is-invalid");
    } else if (lang) {
      lang.classList.remove("is-invalid");
    }

    // NEW: enforce ESOL ≠ English rule
    const ok = enforceESOLLanguageRule({
      programSelect: document.getElementById("program_interest"),
      langSelect: document.getElementById("primary_language")
    });
    if (!ok) isValid = false;

    const anyPlacementSelected =
      (placementYes && placementYes.checked) ||
      (placementNo && placementNo.checked);

    if (!anyPlacementSelected) {
      isValid = false;
      if (placementError) placementError.style.display = "block";
    } else if (placementError) {
      placementError.style.display = "none";
    }
  } else if (placementError) {
    // Non-ESOL → make sure error is hidden
    placementError.style.display = "none";
  }

  // if they have credits, the file is required 
  const creditsFile = document.getElementById("transcript_file");

  if (creditsFile.required && transfer_credits === "yes" && (!creditsFile || !creditsFile.files.length)) {
    isValid = false;
  }

  // only enforce track if this program actually has multiple tracks
  const tracksForProgram = trackOptions[program] || [];
  const trackRequired    = tracksForProgram.length > 1;

  if (trackRequired) {
    if (!track) {
      isValid = false;
    }
  }

  return isValid;
}

async function validateEnrollmentStep() {
  const signatureInput = document.getElementById("signature_data");
  const canvas         = document.getElementById("signature-pad");
  const form = document.getElementById("multi-step-form");
  const fields = [
    "class_start_date",
    "program_completion_date",
    "student_or_guardian_signature_name", // correct ID
    "signature_date"
  ];
  let valid = true;
  
  form.classList.add("was-validated");

  // 0) clear any previous validation errors
  form.querySelectorAll(".is-invalid").forEach(el => {
    el.classList.remove("is-invalid");
  });

  // 1) validate text/date inputs, including checkValidity for min/max on dates
  fields.forEach(id => {
    const el = document.getElementById(id);
    // if empty OR fails browser validity (e.g. date < min or > max)
    if (!el || !el.value.trim() || !el.checkValidity()) {
      valid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });

  // 2) validate the checkbox properly
  const ack = document.getElementById("acknowledge");
  if (!ack.checked) {
    valid = false;
    ack.classList.add("is-invalid");
  } else {
    ack.classList.remove("is-invalid");
  }


  // 4) if there’s any invalid field, scroll the *first* one into view
  if (!valid) {
    const container = document.querySelector(".enrollment-agreement-container");
    // pick the first element with the Bootstrap “is-invalid” class
    const firstError = container.querySelector(".is-invalid");
    if (firstError) {
      firstError.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }

  if (isSignatureEmpty()) {
    // setting a custom error will make the form.invalid-feedback show
    signatureInput.classList.add("is-invalid");
    signatureInput.setCustomValidity(await translateText("Please provide a valid signature."));
    // scroll to the canvas if all others are valid
    if (valid) {
      canvas.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
    valid = false;
  } else {
    const isValidSig = isSignatureValid({ threshold: 0.01});
    if (!isValidSig) {
      signatureInput.classList.add("is-invalid");
      signatureInput.setCustomValidity(await translateText("Please provide a valid signature."));
      if (valid) {
        canvas.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
      }
      valid = false;
    }
    else {
      signatureInput.value = getSignatureDataUrl();
      signatureInput.classList.remove("is-invalid");
    }
  }

  return valid;
}

export function validateAddress() {
  const fields = [
    "street", "state", "city", "country", "zip_code"
  ];
  let isValid = true;
    fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el || !el.value.trim() || !el.value.trim().length) {
      isValid = false;
      el.classList.add("is-invalid");
    } else {
      el.classList.remove("is-invalid");
    }
  });

  const form = document.getElementById("multi-step-form");
  form.classList.add("was-validated");
  return isValid;
}


async function validateDuplicateStudentRecord() {
  const firstNameEl  = document.getElementById("first_name");
  const middleNameEl = document.getElementById("middle_name");
  const lastNameEl   = document.getElementById("last_name");
  const dobEl        = document.getElementById("date_of_birth");

  if (!firstNameEl || !lastNameEl || !dobEl) {
    return true; // nothing to validate
  }

  const first_name  = (firstNameEl.value || "").trim();
  const middle_name = (middleNameEl?.value || "").trim();
  const last_name   = (lastNameEl.value || "").trim();
  const date_of_birth = (dobEl.value || "").trim();

  // Only run if required fields are present and DOB is a valid date
  if (!first_name || !last_name || !date_of_birth) {
    return true;
  }

  try {
    const params = new URLSearchParams({
      first_name,
      middle_name,
      last_name,
      date_of_birth,
    });

    const res = await fetch(
      `/api/method/zoni_edu.services.is_sis_student_record_present?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    );

    const data = await res.json();
    const exists = data?.message === 1;

    // Reset previous custom validity/message
    dobEl.setCustomValidity("");
    dobEl.classList.remove("is-invalid");

    if (exists) {
      const msg = await translateText(
        "A student with the same name and birth date already exists. If it’s you, please contact Admissions. If not, try adding a middle or extra last name."
      );

      // Attach the message to the DOB field (re-using its invalid-feedback container)
      const container = dobEl.closest(".col-md-6") || dobEl.parentElement;
      const feedback =
        container &&
        container.querySelector(".invalid-feedback");

      if (feedback) {
        feedback.innerHTML =
          `<i class="fa-solid fa-circle-exclamation me-1"></i>${msg}`;
      }

      dobEl.setCustomValidity(msg);
      dobEl.classList.add("is-invalid");
      dobEl.reportValidity();
      return false;
    }

    // No duplicate → keep DOB valid
    dobEl.setCustomValidity("");
    dobEl.classList.remove("is-invalid");
    return true;
  } catch (err) {
    console.error("Error checking existing SIS student record:", err);
    // Fail-open: do not block the user if SIS/endpoint is temporarily down
    dobEl.setCustomValidity("");
    dobEl.classList.remove("is-invalid");
    return true;
  }
}


export async function validateProfileStep() {
  const dobEl = document.getElementById("date_of_birth");
  const dobVal = dobEl.value;
  const age    = calculateAge(new Date(dobVal));

  //  ── enforce minimum age of 12 ─────────────────────────────
  if (age < 12) {
    dobEl.setCustomValidity(await translateText("Students must be at least 12 years old"));
    dobEl.reportValidity();
    return false;
  } else {
    dobEl.setCustomValidity("");  // clear previous error
  }
  //  ────────────────────────────────────────────────────────────

  const isMinor = age < 18;
  let valid = true;
  const form = document.getElementById("multi-step-form");
  form.classList.add("was-validated");

  // always required
  const fields = [
    "first_name", "last_name",
    "gender",
    "student_email", "confirm_student_email",
    "student_phone_number",
    "country_of_birth", "date_of_birth", 
    "country_of_citizenship", "race_ethnicity"
  ];

    // only if minor
  if (isMinor) {
    fields.push(
      "guardian_first_name", "guardian_last_name",
      "guardian_email", "confirm_guardian_email",
      "guardian_phone_number",
      "guardian_rel_category", "guardian_relationship",
    );
    if (state.guardian2Added) {
      fields.push(
        "guardian2_first_name", "guardian2_last_name",
        "guardian2_email", "confirm_guardian2_email",
        "guardian2_phone_number",
        "guardian2_rel_category", "guardian2_relationship"
      );
    }
  }

  fields.forEach(id => {
    const el = document.getElementById(id);
    if (!el.checkValidity() || !el.value.trim() || !el.value.trim().length) {
      // add Bootstrap’s “is-invalid” class
      el.classList.add("is-invalid");
      valid = false;
    } else {
      // remove any previous error
      el.classList.remove("is-invalid");
    }
  });

  // 2) async-validate each email field
  const emailIds = ["student_email"];
  if (calculateAge(new Date(document.getElementById("date_of_birth").value)) < 18) {
    // minor → validate guardian(s)
    emailIds.push("guardian_email");
    if (state.guardian2Added) emailIds.push("guardian2_email");
  }

  for (let id of emailIds) {
    const ok = await validateEmail(id);
    if (!ok) valid = false;
    if (id === "student_email" && ok) {
      const isTaken = await validateUsernameIsTaken('student_email');
      if (isTaken) {
        valid = false;
      }
    }
  }

  // 3) validate phone numbers
  const phoneIds = [{instance: "itiApplication", selector: "student_phone_number"}];
  if (isMinor) {
    phoneIds.push({instance: "itiGuardian", selector: "guardian_phone_number"});
    if (state.guardian2Added) phoneIds.push({instance: "itiGuardian2", selector: "guardian2_phone_number"});
  }

  for (let id of phoneIds) {
    const ok = await DynamicContentLoader.isPhoneNumberValid(id.instance);
    if (!ok) {
      valid = false;
      const el = document.getElementById(id.selector);
      // 2) clear any previous custom errors
      if (el) {
        el.setCustomValidity("");
        el.setCustomValidity(await translateText("We couldn’t verify that phone number."));
        el.reportValidity();
        el.classList.add("is-invalid");
      }
    } else {
      const el = document.getElementById(id.selector);
      if (el) {
        el.setCustomValidity("");
        el.classList.remove("is-invalid");
      }
    }
  }

  // 4) check duplicate student record in SIS (only if everything else is OK)
  if (valid) {
    const duplicateOk = await validateDuplicateStudentRecord();
    if (!duplicateOk) {
      valid = false;
    }
  }

  return valid;
}

function getEnrollmentStepEl() {
  // any step that contains the signature canvas OR the acknowledge checkbox
  const steps = Array.from(document.querySelectorAll('.step'));
  return steps.find(s => s.querySelector('#signature-pad') || s.querySelector('#acknowledge')) || null;
}

// Password Validation
export function validatePasswordStep() {
  const pw  = document.getElementById('new-password');
  const pw2 = document.getElementById('confirm-new-password');
  const backendFeedbackElement = document.getElementById('set-password-backend-feedback');
  const form = document.getElementById('multi-step-form');

  // If this template has no password step, don't block navigation.
  if (!pw || !pw2) return true;

  // Helpers (scoped here so they don't leak globals)
  const setIcon = (id, ok) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('fa-check-circle', ok);
    el.classList.toggle('fa-times-circle', !ok);
    el.style.color = ok ? '#78981fFF' : '#ef3a437f';
  };

  const updateBackendFeedback = (message) => {
    if (!backendFeedbackElement) return;
    backendFeedbackElement.innerHTML = '';
    if (!message || !message.feedback) return;
    const { warning, suggestions } = message.feedback;
    if (warning) backendFeedbackElement.innerHTML += `<small>${warning}</small><br>`;
    if (Array.isArray(suggestions)) {
      suggestions.forEach(s => backendFeedbackElement.innerHTML += `<small>${s}</small><br>`);
    }
  };

  const runBackendValidation = async (password) => {
    // debounce/in-flight guard
    if (passwordState.backendInFlight) return;

    passwordState.backendInFlight = true;
    if (backendFeedbackElement) {
      backendFeedbackElement.innerHTML = `<small>${await translateText("Checking password strength…")}</small>`;
    }
    setIcon('new-backend', false);

    try {
      const endpointUrl = '/api/method/zoni_edu.zoni_edu.doctype.account_activation.account_activation.validate_password_strength';
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Frappe-CSRF-Token': frappe.csrf_token
        },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      const message = data?.message || {};
      passwordState.meetsBackend = !!message.meets_requirements;
      passwordState.lastBackendValue = password;
      updateBackendFeedback(message);
      setIcon('new-backend', passwordState.meetsBackend);
    } catch (err) {
      console.error('Error validating password:', err);
      passwordState.meetsBackend = false;
      if (backendFeedbackElement) {
        backendFeedbackElement.innerHTML = `<small>${await translateText("Could not verify password right now. Please try again.")}</small>`;
      }
      setIcon('new-backend', false);
    } finally {
      passwordState.backendInFlight = false;
    }
  };

  // 1) LOCAL CHECKS
  const v1 = pw.value || '';
  const v2 = pw2.value || '';

  const hasLen = v1.length >= 8 && v1.length <= 64;
  const hasL   = /[a-z]/.test(v1);
  const hasU   = /[A-Z]/.test(v1);
  const hasN   = /[0-9]/.test(v1);
  const hasS   = /[^\w\s]/.test(v1);       // symbol
  const match  = v1 === v2 && v2.length > 0;

  setIcon('new-8char', hasLen);
  setIcon('new-lcase', hasL);
  setIcon('new-ucase', hasU);
  setIcon('new-num',   hasN);
  setIcon('new-symbol',hasS);
  setIcon('new-pwmatch', match);

  passwordState.meetsLocal = hasLen && hasL && hasU && hasN && hasS && match;

  // Clear previous invalids and then mark what failed
  [pw, pw2].forEach(el => el.classList.remove('is-invalid'));

  if (!passwordState.meetsLocal) {
    if (!(hasLen && hasL && hasU && hasN && hasS)) pw.classList.add('is-invalid');
    if (!match) pw2.classList.add('is-invalid');
    form?.classList.add('was-validated');
    // Focus first offending field
    const firstBad = pw.classList.contains('is-invalid') ? pw : (pw2.classList.contains('is-invalid') ? pw2 : null);
    if (firstBad) firstBad.focus();
    return false;
  }

  // 2) BACKEND CHECK GATING (no button disabling)
  const needsBackendCheck =
    !passwordState.meetsBackend || passwordState.lastBackendValue !== v1;

  if (needsBackendCheck) {
    // Fire-and-forget backend check; user can click Continue again after it completes.
    runBackendValidation(v1);
    // Block navigation until we have a positive backend result.
    return false;
  }

  // All good — locals + backend already OK for this value
  return true;
}

export function updateRequirement(element, isValid) {
  if (isValid) {
    element.classList.add("valid");
    element.querySelector(".icon").textContent = "✓";
  } else {
    element.classList.remove("valid");
    element.querySelector(".icon").textContent = "❌";
  }
}

export function isValidPassword(password) {
  return (
    password.length >= 8 && /[A-Z]/.test(password) && /[0-9]/.test(password)
  );
}

// Navigation Functions

export function goToStep(stepNumber) {
  const prev = document.querySelector(`.step-${state.currentStep}`);
  const next = document.querySelector(`.step-${stepNumber}`);
  prev.style.display = "none";
  next.style.display = "block";
  state.currentStep = stepNumber;
  updateProgressBar();
  document.getElementById("multi-step-form").classList.remove("was-validated");

  const formContainer = document.getElementById("form-container");
  formContainer.scrollTop = 0;
  window.scrollTo({ top: formContainer.offsetTop - 20, behavior: 'smooth' });

  const type = getStepType(stepNumber);
  if (type !== 'enrollment') {
    const firstField = next.querySelector("input, select, textarea");
    if (firstField) firstField.focus();
  } else {
    // we just entered the enrollment step; if the canvas is visible, init now
    setTimeout(() => initEnrollmentWhenVisible(), 0);
    primeZahsCapToken();
  }
}

// Helper Functions
async function fetchCountryName(code) {
  if (!code) {
    return "";
  }

  try {
    const res = await fetch(
      `/api/method/zoni_edu.services.get_country_name?code=${encodeURIComponent(code)}`
    );
    const { message } = await res.json();
    return message || "";
  } catch (e) {
    console.error("Failed to fetch country name", e);
    return "";
  }
}

async function fetchAffiliations() {
  const res = await fetch('/api/method/zoni_edu.services.zahs_affiliations');
  const { message } = await res.json();
  return message;
}


function buildAffiliationMaps() {
  // reset
  programOptions = [];
  trackOptions   = {};

  // 1) pick out each “top-level” program row
  //    (where text === program)
  const programIdByName = {};
  affiliations.forEach(a => {
    if (a.text === a.program) {
      programOptions.push({ value: a.value, text: a.text });
      trackOptions[a.value] = [];
      programIdByName[a.program] = a.value;
    }
  });

  // 2) assign all the actual tracks into the right bucket
  affiliations.forEach(a => {
    // skip the top-level rows
    if (a.text !== a.program) {
      const pid = programIdByName[a.program];
      if (pid) {
        trackOptions[pid].push({
          value: a.value,
          text:  a.text
        });
      }
    }
  });
}


function prefillDraftCreditsFromAffiliation() {
  // Only run if we actually have something from the server
  if (!initialAffiliationFromServer) return;

  const progSel  = document.getElementById("program_interest");
  const trackSel = document.getElementById("track_interest");
  if (!progSel || !trackSel) return;

  const affiliationValue = String(initialAffiliationFromServer);

  // 1) Find the affiliation row that matches this value
  const row = affiliations.find(a => String(a.value) === affiliationValue);
  if (!row) {
    // Fallback: treat it as a program id directly
    progSel.value = affiliationValue;
    progSel.dispatchEvent(new Event("change"));
    const opt = Array.from(trackSel.options).find(o => o.value === affiliationValue);
    if (opt) trackSel.value = affiliationValue;
    return;
  }

  // 2) Find the *program* row for this affiliation
  //    (top-level program rows have text === program)
  const programRow = affiliations.find(
    a => a.program === row.program && a.text === row.program
  );
  const programValue = programRow ? String(programRow.value) : affiliationValue;

  // 3) Set the program select to this program and trigger change
  progSel.value = programValue;
  progSel.dispatchEvent(new Event("change"));

  // 4) After the change, the track options are rebuilt.
  //    Now select the original affiliation as the track if present.
  const trackOpt = Array.from(trackSel.options).find(
    o => String(o.value) === affiliationValue
  );
  if (trackOpt) {
    trackSel.value = affiliationValue;
  }
}

function populateProgramSelect() {
  const sel = document.getElementById('program_interest');
  if (!sel) return;

  sel.innerHTML = [
    `<option value="">${selectTranslation}</option>`,
    ...programOptions.map(p =>
      `<option value="${p.value}">${p.text}</option>`
    )
  ].join('');
}

function wireUpProgramTrackHandlers() {
  const progSel  = document.getElementById('program_interest');
  const trackSel = document.getElementById('track_interest');
  if (!progSel || !trackSel) return;

  progSel.addEventListener('change', () => {
    const pid    = progSel.value;
    const tracks = trackOptions[pid] || [];

    trackSel.innerHTML = [
      `<option value="">${selectTranslation}</option>`,
      ...tracks.map(t =>
        `<option value="${t.value}">${t.text}</option>`
      )
    ].join('');

    // disable if no tracks for this program
    trackSel.disabled = tracks.length === 0;
  });

  // trigger initial fill (in case you pre-select)
  progSel.dispatchEvent(new Event('change'));
}

async function fetchRelationshipOptions() {
  const res = await fetch('/api/method/zoni_edu.services.zahs_guardian_parent_relationships');
  const { message } = await res.json();
  return message.reduce((acc, { value, text, category }) => {
    if (!acc[category]) acc[category] = [];
    acc[category].push({ value, text });
    return acc;
  }, {});
}

function populateRelationshipCategories() {
  const categories = Object.keys(relationshipOptions);
  ['guardian_rel_category', 'guardian2_rel_category'].forEach(selectId => {
    const sel = document.getElementById(selectId);
    if (!sel) return;

    // Remember any existing value (from server or previous user input)
    const previousValue = sel.value;

    sel.innerHTML = [
      `<option value="">${selectTranslation}</option>`,
      ...categories.map(cat =>
        `<option value="${cat}">${
          cat[0].toUpperCase() + cat.slice(1)
        }</option>`
      )
    ].join('');

    if (previousValue) {
      sel.value = previousValue;
    }
  });
}

function wireUpRelationshipHandlers() {
  const pairs = [
    ['guardian_rel_category', 'guardian_relationship'],
    ['guardian2_rel_category', 'guardian2_relationship'],
  ];

  pairs.forEach(([catId, relId]) => {
    const cat = document.getElementById(catId);
    const rel = document.getElementById(relId);
    if (!cat || !rel) return;

    // Preserve the relationship value as rendered by the template
    const initialRelValue = rel.value;

    const updateRelationshipOptions = () => {
      const opts = relationshipOptions[cat.value] || [];
      const previous = rel.value || initialRelValue;

      rel.innerHTML = `<option value="">${selectTranslation}</option>`;
      opts.forEach(({ value, text }) => {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = text;
        rel.appendChild(o);
      });
      rel.disabled = opts.length === 0;

      // Re-select the previous relationship if it still exists in this category
      if (previous && opts.some((o) => o.value === previous)) {
        rel.value = previous;
      }
    };

    cat.addEventListener('change', updateRelationshipOptions);

    // On initial load (resume), run once if a category is already set
    if (cat.value) {
      updateRelationshipOptions();
    }
  });
}

function setDOBMax() {
  const dob = document.getElementById("date_of_birth");
  if (!dob) return;

  const today = new Date();
  // subtract 12 years
  const year  = today.getFullYear() - 12;
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day   = String(today.getDate()).padStart(2, "0");

  dob.max = `${year}-${month}-${day}`; 
}

export async function validateEmail(id) {
  const el = document.getElementById(id);
  // 1) run your local-regex check first
  const pattern = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/i;
  if (!pattern.test(el.value.trim())) {
    //el.setCustomValidity("Please enter a valid email address.");
    el.reportValidity();
    return false;
  }
  // 2) clear any previous custom error so the backend can run
  el.setCustomValidity("");
  // 3) your existing backend check
  const ok = await validateEmailOnBackend(id);
  if (!ok) {
    if (!el.validationMessage) {
      el.setCustomValidity(await translateText("We couldn’t verify that email address."));
    }
    el.reportValidity();
    return false;
  }
  // 4) all good
  el.setCustomValidity("");
  return true;
}


async function validateEmailOnBackend(emailID) {
  const el = document.getElementById(emailID);
  if (el && el.value.trim()) {
    resetInvalidFeedbackMessage(el);

    try {
      if (emailID === "student_email") {
        const params = new URLSearchParams({
          email: el.value.trim(),
        });
        const currentToken = draftApp.token || String(window.token || "").trim();
        const currentName = draftApp.name || "";

        if (currentToken) {
          params.set("application_token", currentToken);
        }
        if (currentName) {
          params.set("application_name", currentName);
        }

        const response = await fetch(
          `/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.validate_zahs_student_email?${params.toString()}`,
          { headers: { "Content-Type": "application/json" } }
        );
        const { message } = await response.json();
        if (!message?.format_valid) {
          el.classList.add("is-invalid");
          el.classList.remove("is-valid");
          return false;
        }

        if (message?.duplicate_application) {
          const duplicateMessage = message?.message || await translateText(
            "A previous ZAHS application already exists for that email."
          );

          setInvalidFeedbackMessage(el, duplicateMessage, message);
          el.setCustomValidity(duplicateMessage);
          el.classList.add("is-invalid");
          el.classList.remove("is-valid");
          el.reportValidity();
          return false;
        }

        el.setCustomValidity("");
        el.classList.remove("is-invalid");
        return true;
      }

      const response = await fetch(
        `/api/method/zoni_edu.services.validate_email?email=${encodeURIComponent(el.value.trim())}`,
        { headers: { "Content-Type": "application/json" } }
      );
      const { message } = await response.json();
      if (message === 0) {
        //document.getElementById("multi-step-form").classList.remove("was-validated");
        el.classList.add("is-invalid");
        el.classList.remove("is-valid");
        console.error("Email validation failed:", message);
        return false;
      } else {
        el.classList.remove("is-invalid");
        return true;
      }
    } catch (error) {
      console.error("Error validating email:", error);
      el.classList.add("is-invalid");
      console.error("Email validation failed due to network error:", error);
      return false;
    }
  }
  return true;
}

function getInvalidFeedbackElement(el) {
  const container = el.closest(".col-md-6") || el.parentElement;
  return container ? container.querySelector(".invalid-feedback") : null;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildDuplicateActionLink(url, label) {
  const href = String(url || "").trim();
  const text = String(label || "").trim();
  if (!href || !text) {
    return "";
  }

  const external = !href.startsWith("mailto:") && !href.startsWith("tel:");
  const target = external ? ' target="_blank" rel="noopener noreferrer"' : "";

  return `
    <a class="d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold"
       href="${escapeHtml(href)}"${target}>
      ${escapeHtml(text)}
    </a>
  `;
}

function buildExistingApplicationMessageHtml(message, details = {}) {
  const safeMessage = escapeHtml(message).replace(/\n/g, "<br/>");
  const actions = [
    buildDuplicateActionLink(details.action_url, details.action_label),
    buildDuplicateActionLink(
      details.secondary_action_url,
      details.secondary_action_label
    ),
  ].filter(Boolean);

  if (!actions.length) {
    return `<i class="fa-solid fa-circle-exclamation me-1"></i>${safeMessage}`;
  }

  return `
    <i class="fa-solid fa-circle-exclamation me-1"></i>
    <span>${safeMessage}</span>
    <div class="mt-2 d-flex flex-wrap gap-3">
      ${actions.join("")}
    </div>
  `;
}

async function getExistingApplicationResponseTitle(details = {}) {
  if (details?.title) {
    return String(details.title);
  }

  switch (details?.resolution) {
    case "resume_application":
      return translateText("Resume saved application");
    case "complete_post_payment_setup":
      return translateText("Finish setup after payment");
    case "access_existing_account":
      return translateText("Access your existing account");
    case "contact_support":
      return translateText("Contact admissions");
    default:
      return translateText("Existing application found");
  }
}

function setInvalidFeedbackMessage(el, message, details = {}) {
  const feedback = getInvalidFeedbackElement(el);
  if (!feedback) return;

  if (!feedback.dataset.defaultHtml) {
    feedback.dataset.defaultHtml = feedback.innerHTML;
  }

  feedback.innerHTML = buildExistingApplicationMessageHtml(message, details);
}

function resetInvalidFeedbackMessage(el) {
  const feedback = getInvalidFeedbackElement(el);
  if (!feedback || !feedback.dataset.defaultHtml) return;

  feedback.innerHTML = feedback.dataset.defaultHtml;
}

async function validateUsernameIsTaken(usernameID) {
  const el = document.getElementById(usernameID);
  if (el && el.value.trim()) {
    try {
      const response = await fetch(
        `/api/method/zoni_edu.services.is_sis_username_taken?username=${encodeURIComponent(el.value.trim())}`,
        { headers: { "Content-Type": "application/json" } }
      );
      const { message } = await response.json();

      if (message === 1) {
        const msg = await translateText(
          "This email is already associated with an existing user. Please choose a different one"
        );

        setInvalidFeedbackMessage(el, msg);

        el.setCustomValidity(msg);
        el.classList.add("is-invalid");
        el.classList.remove("is-valid");

        // optional: if you want the native tooltip too
        el.reportValidity();

        return true;  // "taken"
      } else {
        // clear custom error & restore default feedback text if you want
        el.setCustomValidity("");
        resetInvalidFeedbackMessage(el);
        el.classList.remove("is-invalid");
        return false; // "not taken"
      }
    } catch (error) {
      console.error("Error validating username:", error);
      el.classList.add("is-invalid");
      return false;
    }
  }
  return false;
}

export function calculateAge(birthDate) {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

export function updateStudentNameDisplay() {
  const firstName = document.getElementById("first_name").value;
  elements.studentNameSpan.textContent = firstName;
}

export function hideAllSteps() {
  document.querySelectorAll(".step-1, .step-2, .step-3, .step-4, .step-5").forEach((el) => {
    el.style.display = "none";
  });
}

export function updateProgressBar() {
  if (!elements.progressBar) return;
  const progressPercentage =
    ((state.currentStep - 1) / (config.totalSteps - 1)) * 100;
  elements.progressBar.style.width = progressPercentage + "%";
}

// Component Initializers
export function setupCreditsStepBehavior() {
  const transfer_creditsSelect = document.getElementById("transfer_credits");
  const uploadContainer  = document.getElementById("creditsUploadContainer");
  const programSelect    = document.getElementById("program_interest");
  const trackContainer   = document.getElementById("track-interest-container");
  const trackSelect      = document.getElementById("track_interest");
  const langContainer = document.getElementById("primary-language-container");
  const langSelect    = document.getElementById("primary_language");
  const placementContainer = document.getElementById("placement-test-container");
  const placementRadios    = document.querySelectorAll('input[name="placement_choice"]');
  const placementError     = placementContainer
    ? placementContainer.querySelector(".invalid-feedback")
    : null;


  if (programSelect && trackSelect) {
    const fromTrack   = (trackSelect.value || "").trim();
    const fromProgram = (programSelect.value || "").trim();
    // In the template, both program_interest and track_interest are rendered
    // with zahs_application.affiliation, so we just keep a single canonical value.
    initialAffiliationFromServer = fromTrack || fromProgram || null;
  }

  if (
    !transfer_creditsSelect || !uploadContainer || !programSelect ||
    !trackContainer || !trackSelect || !langContainer || !langSelect ||
    !placementContainer || !placementRadios.length
  ) {
    return;
  }

  const updateLanguageVisibility = () => {
    if (!langContainer || !langSelect) return;
    const isESOL = programSelect.value === "5";
    langContainer.style.display = isESOL ? "block" : "none";

    if (isESOL) {
      langSelect.setAttribute("required", "");
    } else {
      // clear and unrequire if not ESOL
      langSelect.removeAttribute("required");
      if (window.$ && $(langSelect).data("select2")) {
        $(langSelect).val("").trigger("change");
      } else {
        langSelect.value = "";
      }
      // also clear any validation state
      langSelect.classList.remove("is-invalid");
      langSelect.setCustomValidity("");
    }
  };

  const updatePlacementVisibility = () => {
    const isESOL = programSelect.value === "5";

    placementContainer.style.display = isESOL ? "block" : "none";

    placementRadios.forEach((radio) => {
      if (isESOL) {
        radio.removeAttribute("disabled");
      } else {
        radio.checked = false;
        radio.setAttribute("disabled", "disabled");
      }
    });

    if (placementError) {
      placementError.style.display = "none";
    }
  };

  programSelect.addEventListener("change", function () {
    const pid    = this.value;

    document.querySelectorAll("#program-info-container .program-info").forEach(el => {
      el.classList.add("d-none");
    });

    const activeInfo = document.getElementById(`program-info-${pid}`);
    if (activeInfo) {
      activeInfo.classList.remove("d-none");
    }

    const tracks = trackOptions[pid] || [];
    const show   = tracks.length > 1;

    trackContainer.style.display = show ? "block" : "none";
    if (show) {
      trackSelect.setAttribute("required", "");
    } else {
      trackSelect.removeAttribute("required");
      trackSelect.value = "";
    }

    updateLanguageVisibility();
    updatePlacementVisibility();
    // enforce the ESOL + language rule on any program change
    enforceESOLLanguageRule({ programSelect, langSelect });
  });

  // re-check when language changes (supports native + Select2)
  langSelect.addEventListener("change", () => {
    enforceESOLLanguageRule({ programSelect, langSelect });
  });
  if (window.$ && $(langSelect).data("select2")) {
    $(langSelect).on("select2:select", () => {
      enforceESOLLanguageRule({ programSelect, langSelect });
    });
  }

  // on init, in case there's a pre-selected program
  programSelect.dispatchEvent(new Event('change'));

  transfer_creditsSelect.addEventListener("change", function () {
    const showUpload = this.value === "yes";
    uploadContainer.style.visibility = showUpload ? "visible" : "hidden";
    uploadContainer.style.height     = showUpload ? "auto" : "0";
    uploadContainer.style.overflow   = showUpload ? "visible" : "hidden";
  });
}

export function setupDragAndDropUpload() {
  const dropArea = document.getElementById("drop-area");
  const fileInput = document.getElementById("transcript_file");
  const feedback = document.getElementById("upload-feedback");

  if (!dropArea || !fileInput || !feedback) return;

  ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
    dropArea.addEventListener(eventName, (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  dropArea.addEventListener("dragover", () => {
    dropArea.classList.add("border-primary", "bg-light");
  });

  dropArea.addEventListener("dragleave", () => {
    dropArea.classList.remove("border-primary", "bg-light");
  });

  dropArea.addEventListener("drop", (e) => {
    dropArea.classList.remove("border-primary", "bg-light");
    const files = e.dataTransfer.files;
    handleFileUpload(files, fileInput, feedback);
  });

  fileInput.addEventListener("change", () => {
    handleFileUpload(fileInput.files, fileInput, feedback);
  });
}

export async function handleFileUpload(files, fileInput, feedback) {
  if (files.length > 0) {
    fileInput.files = files;
    feedback.style.display = "block";
    feedback.textContent = await translateText("Selected: ") + files[0].name;
  }
}

export function toggleAssistanceDetails(show) {
  document.getElementById("assistanceDetails").style.display = show
    ? "block"
    : "none";
}
export async function collectFormData() {
  const formData = {
    profile: {
      first_name: document.getElementById("first_name").value,
      last_name: document.getElementById("last_name").value,
      middle_name: document.getElementById("middle_name").value,
      gender: document.getElementById("gender").value,
      student_email: document.getElementById("student_email").value,
      confirm_student_email: document.getElementById("confirm_student_email").checked,
      student_phone_number: DynamicContentLoader.getPhoneNumber('itiApplication'),
      country_of_birth: await fetchCountryName(document.getElementById("country_of_birth").value),
      date_of_birth: document.getElementById("date_of_birth").value,
      country_of_citizenship: await fetchCountryName(document.getElementById("country_of_citizenship").value),
      race_ethnicity: document.getElementById("race_ethnicity").value,
      subscription: document.getElementById("subscription").value,
    },
    guardian: {
      first_name: document.getElementById("guardian_first_name").value,
      last_name: document.getElementById("guardian_last_name").value,
      email: document.getElementById("guardian_email").value,
      confirm_guardian_email: document.getElementById("confirm_guardian_email").checked,
      phone_number: DynamicContentLoader.getPhoneNumber('itiGuardian'),
      relationship_category: document.getElementById("guardian_rel_category").value,
      relationship: document.getElementById("guardian_relationship").value,
    },
    guardian2: {
      first_name: document.getElementById("guardian2_first_name").value,
      last_name: document.getElementById("guardian2_last_name").value,
      email: document.getElementById("guardian2_email").value,
      confirm_guardian2_email: document.getElementById("confirm_guardian2_email").checked,
      phone_number: DynamicContentLoader.getPhoneNumber('itiGuardian2'),
      relationship_category: document.getElementById("guardian2_rel_category").value,
      relationship: document.getElementById("guardian2_relationship").value,
    },
    address: {
      street: document.getElementById("street").value,
      city: document.getElementById("city").value,
      state: document.getElementById("state").value,
      country: await fetchCountryName(document.getElementById("country").value),
      zip_code: document.getElementById("zip_code").value,
    },
    // Password is now collected after payment on the payment status page

    credits: {
      program_interest: document.getElementById("program_interest").value,
      track_interest: document.getElementById("track_interest").value,
      primary_language: document.getElementById("primary_language").value || null,
      primary_language_text: document.getElementById("primary_language").selectedOptions[0]?.text || null,
      application_page_language: document.getElementById("application_page_language").value,
      grade_level: document.getElementById("grade_level").value,
      transfer_credits: document.getElementById("transfer_credits").value,
      transcript_file: document.getElementById("transcript_file").files[0]?.name || null,
      needs_assistance: document.getElementById("needs_assistance").value,
      placement_choice: document.querySelector('input[name="placement_choice"]:checked')
        ? document.querySelector('input[name="placement_choice"]:checked').value
        : null,
    },

    enrollment: {
      class_start_date: document.getElementById("class_start_date").value,
      program_completion_date: document.getElementById("program_completion_date").value,
      acknowledge: document.getElementById("acknowledge").checked,
      student_or_guardian_signature_name: document.getElementById("student_or_guardian_signature_name").value,
      signature_date: document.getElementById("signature_date").value,
    }
  };

  return formData;
}

export function showSuccessMessage() {
  // Create a success modal or message

  document.getElementById("enrollment-success").style.display = "block"; // Show success
  document.getElementById("progress-div").style.display = "none"; // Hide progress bar
}

/**
 * @function generateUUID
 * @returns {string} 
 *         
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}


function scrubPasswordFieldsForResume(doc) {
  if (!isResumeMode()) {
    // New application flow → keep current behavior
    return doc;
  }

  const pw  = (doc.password || "").trim();
  const pw2 = (doc.confirm_password || "").trim();

  // If user did NOT type a new password during this session,
  // do not send password fields at all → backend won't change them.
  if (!pw && !pw2) {
    delete doc.password;
    delete doc.confirm_password;
  }

  return doc;
}


export async function submitFormData(formData) {
  const submitBtn = document.getElementById("submit-enroll-btn");
  const originalSubmitHtml = submitBtn.innerHTML;
  submitBtn.disabled = true;
  submitBtn.innerHTML =
    `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${await translateText("Submitting...")}`;

  if (!draftApp.token) {
    updateDraftAppIdentity(null, generateUUID());
  }

  const doc = {
    doctype: "ZAHS Application",
    first_name:                   formData.profile.first_name,
    last_name:                    formData.profile.last_name,
    middle_name:                  formData.profile.middle_name,
    gender:                       formData.profile.gender,
    student_email:                formData.profile.student_email,
    confirm_student_email:        formData.profile.confirm_student_email ? 1 : 0,
    student_phone_number:         formData.profile.student_phone_number,
    country_of_birth:             formData.profile.country_of_birth,
    date_of_birth:                formData.profile.date_of_birth,
    country_of_citizenship:       formData.profile.country_of_citizenship,
    race_ethnicity:               formData.profile.race_ethnicity,

    guardian_first_name:          formData.guardian.first_name,
    guardian_last_name:           formData.guardian.last_name,
    guardian_email:               formData.guardian.email,
    confirm_guardian_email:       formData.guardian.confirm_guardian_email ? 1 : 0,
    guardian_phone_number:        formData.guardian.phone_number,
    guardian_relationship:        formData.guardian.relationship,

    guardian2_first_name:         formData.guardian2.first_name,
    guardian2_last_name:          formData.guardian2.last_name,
    guardian2_email:              formData.guardian2.email,
    confirm_guardian2_email:      formData.guardian2.confirm_guardian2_email ? 1 : 0,
    guardian2_phone_number:       formData.guardian2.phone_number,
    guardian2_relationship:       formData.guardian2.relationship,
    subscription:                 formData.profile.subscription == 1 ? 1 : 0,

    street:                       formData.address.street,
    city:                         formData.address.city,
    state:                        formData.address.state,
    country:                      formData.address.country,
    zip_code:                     formData.address.zip_code,

    // Password is now collected after payment on the payment status page

    affiliation:                  formData.credits.track_interest || formData.credits.program_interest,
    primary_language:             formData.credits.primary_language,
    primary_language_text:        formData.credits.primary_language_text,
    application_page_language:    formData.credits.application_page_language,
    take_esol_placement_exam:
      formData.credits.program_interest === "5" &&
      formData.credits.placement_choice === "take_esol_placement_exam"
        ? 1
        : 0,
    skip_esol_placement_exam:
      formData.credits.program_interest === "5" &&
      formData.credits.placement_choice === "skip_esol_placement_exam"
        ? 1
        : 0,
    grade_level:                  formData.credits.grade_level,
    transfer_credits:             formData.credits.transfer_credits === "yes" ? "Yes" : "No",
    //transcript:                   formData.credits.transcript_file,
    needs_assistance:              formData.credits.needs_assistance === "yes" ? "Yes" : "No",

    class_start_date:             formData.enrollment.class_start_date,
    program_completion_date:      formData.enrollment.program_completion_date,
    student_or_guardian_signature_name: formData.enrollment.student_or_guardian_signature_name,
    acknowledge:                  formData.enrollment.acknowledge ? 1 : 0,
    signature_date:               formData.enrollment.signature_date,
    token:                        draftApp.token,
  };

  // Password is now collected after payment, no need to scrub password fields
  void doc;

  let redirecting = false;
  try {
    const submitResponse = await fetch(
      "/api/method/zoni_edu.zoni_lms.doctype.zahs_application.zahs_application.submit_zahs_application",
      {
        method: "POST",
        headers: await buildZahsProtectedHeaders({
          Accept: "application/json",
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          token: draftApp.token,
          step_index: state.currentStep,
          data: JSON.stringify(formData),
        }),
      }
    );
    const submitPayload = await submitResponse.json().catch(() => ({}));
    if (!submitResponse.ok) {
      throw new Error(
        extractFrappeErrorMessage(submitPayload)
        || await translateText("Submission failed.")
      );
    }

    const submitMessage = submitPayload.message || {};
    if (submitMessage.status === "existing_application_handled") {
      const recoveryTitle = await getExistingApplicationResponseTitle(
        submitMessage
      );
      const recoveryMessage = submitMessage.message || await translateText(
        "A previous ZAHS application already exists for that email."
      );
      const recoveryHtml = buildExistingApplicationMessageHtml(
        recoveryMessage,
        submitMessage
      );
      await showAlertOrFallback(recoveryHtml, { title: recoveryTitle });
      return;
    }

    const docname = submitMessage.name;
    if (!docname) {
      throw new Error(await translateText("Application save failed."));
    }

    updateDraftAppIdentity(docname, submitMessage.token || draftApp.token);

    const sigDataUrl = document.getElementById("signature_data").value;
    if (sigDataUrl) {
      const signatureBlob = await (await fetch(sigDataUrl)).blob();
      await uploadZahsApplicationAttachment(
        "signature_data",
        signatureBlob,
        "signature.png"
      );
    }

    const fileInput = document.getElementById("transcript_file");
    if (fileInput?.files?.length) {
      await uploadZahsApplicationAttachment(
        "transcript_file",
        fileInput.files[0]
      );
    }

    const paymentResponse = await fetch(
      `/api/method/zoni_edu.services.get_zahs_app_payment_url?docname=${encodeURIComponent(docname)}`,
      {
        headers: await buildZahsProtectedHeaders({
          Accept: "application/json",
        }),
      }
    );
    const paymentPayload = await paymentResponse.json().catch(() => ({}));
    if (!paymentResponse.ok) {
      throw new Error(
        extractFrappeErrorMessage(paymentPayload)
        || await translateText("Failed to continue to payment.")
      );
    }

    const paymentUrl = paymentPayload.message;
    if (!paymentUrl) {
      throw new Error(await translateText("No payment URL found."));
    }

    redirecting = true;
    submitBtn.innerHTML =
      `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${await translateText("Redirecting to payment...")}`;
    window.location.href = paymentUrl;
  } catch (err) {
    console.error(err);
    const errorMessage = err instanceof Error && err.message
      ? err.message
      : await translateText("Submission failed.");
    await showAlertOrFallback(errorMessage, {
      title: await translateText("Unable to continue"),
    });
  } finally {
    if (!redirecting) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalSubmitHtml;
    }
  }
}

function setupCountryStateLinkage() {
  const $country = $('#country');
  const $state   = $('#state');
  if (!$country.length || !$state.length) return;

  let ignoreLinkage = false;

  const countryHandler = () => {
    if (ignoreLinkage) return;
    ignoreLinkage = true;
    if ($country.val() !== 'US') {
      // force “Other” on the state select without looping back
      $state.val('00').trigger('change');
    }
    ignoreLinkage = false;
  };

  const stateHandler = () => {
    if (ignoreLinkage) return;
    ignoreLinkage = true;
    if ($state.val() === '00') {
      // force “Other” on the country select without looping back
      $country.val('Other').trigger('change');
    } else {
      // force “US” on the country select without looping back
      $country.val('US').trigger('change');
    }
    ignoreLinkage = false;
  };

  // catch both the native change and Select2’s own select event
  $country.on('change', countryHandler);
  $country.on('select2:select', countryHandler);

  $state.on('change', stateHandler);
  $state.on('select2:select', stateHandler);
}

function initEnrollmentWhenVisible() {
  const stepEl = getEnrollmentStepEl();
  if (!stepEl) return;

  // Only init when the canvas is actually laid out (non-zero size)
  const tryInit = () => {
    const canvas = stepEl.querySelector('#signature-pad');
    if (!canvas) return false;
    const visible = stepEl.style.display !== 'none' && canvas.offsetWidth > 0 && canvas.offsetHeight > 0;
    if (!visible) return false;

    // safe to initialize now
    initSignaturePad();
    setDatesToToday();
    setProgramCompletionDateRange();
    updateSuggestedCompletionDate();
    updateSignatureName();
    return true;
  };

  // 1) try immediately (in case we entered directly)
  if (tryInit()) return;

  // 2) watch for the step becoming visible (style/class changes)
  const mo = new MutationObserver(() => {
    if (stepEl.style.display === 'block') {
      // give layout a tick
      setTimeout(() => {
        if (tryInit()) mo.disconnect();
      }, 0);
    }
  });
  mo.observe(stepEl, { attributes: true, attributeFilter: ['style', 'class'] });

  // 3) also listen to window resizes (mobile keyboards, rotations, etc.)
  const onResize = () => {
    if (tryInit()) window.removeEventListener('resize', onResize);
  };
  window.addEventListener('resize', onResize);
}

function initSubmitButton() {
  const submitBtn = document.getElementById("submit-enroll-btn");
  const multiStepForm = document.getElementById("multi-step-form");
  const signatureInput = document.getElementById("signature_data");
  if (!multiStepForm || !signatureInput || !submitBtn) return;
  submitBtn.addEventListener("click", async () => {
    if (submitBtn.disabled) return;              // guard
    submitBtn.disabled = true;                   // disable immediately
    multiStepForm.classList.add("was-validated");
    if (!(await validateEnrollmentStep())) {
      submitBtn.disabled = false;                  // re-enable on failure
      return;
    }
    const formData = await collectFormData();
    formData.signature = signatureInput.value;
    await submitFormData(formData);
  });
}

function setDatesToToday() {
  const today = new Date().toISOString().split('T')[0];
  ['class_start_date', 'signature_date'].forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.value = today;
    }
  });
}

function setProgramCompletionDateRange() {
  const el = document.getElementById("program_completion_date");
  if (!el) return;

  const today = new Date();

  // min = 30 days from now
  const minDate = new Date(today);
  minDate.setDate(minDate.getDate() + 30);

  // max = 6 years from now
  const maxDate = new Date(today);
  maxDate.setFullYear(maxDate.getFullYear() + 6);

  const fmt = d => d.toISOString().split("T")[0];

  el.min = fmt(minDate);
  el.max = fmt(maxDate);
}

function updateSuggestedCompletionDate() {
  const grade = parseInt(document.getElementById('grade_level').value, 10);
  const el    = document.getElementById('program_completion_date');
  if (!el || isNaN(grade)) return;

  // today
  const today = new Date();

  // mapping grades → suggested years
  const map = {
    9:  2.5,
    10: 2.0,
    11: 1.5,
    12: 1.0,
  };
  let years = map[grade] ?? 2.5;

  // build the suggested date
  const suggested = new Date(today);
  suggested.setMonth(suggested.getMonth() + Math.round(years * 12));

  // clamp to your existing min/max
  const min = new Date(today);    min.setDate(min.getDate() + 30);
  const max = new Date(today);    max.setFullYear(max.getFullYear() + 6);

  if (suggested < min) suggested.setTime(min.getTime());
  if (suggested > max) suggested.setTime(max.getTime());

  el.value = suggested.toISOString().slice(0,10);
}

function updateSignatureName() {
  const dobVal = document.getElementById('date_of_birth').value;
  if (!dobVal) return;
  const age = calculateAge(new Date(dobVal));

  let fullName = '';
  if (age < 18) {
    // minor → use guardian
    const g1 = document.getElementById('guardian_first_name').value.trim();
    const g2 = document.getElementById('guardian_last_name').value.trim();
    fullName = [g1, g2].filter(Boolean).join(' ');
  } else {
    // adult → use student
    const f = document.getElementById('first_name').value.trim();
    const m = document.getElementById('middle_name').value.trim();
    const l = document.getElementById('last_name').value.trim();
    fullName = [f, m, l].filter(Boolean).join(' ');
  }

  const sig = document.getElementById('student_or_guardian_signature_name');
  if (sig) sig.value = fullName;
}

/**
 * Fetch the user’s location and prefill country, state, city & zip.
 */
async function prefillAddressFromIP() {
  try {
    let endpoint = '/api/method/zoni_edu.services.loc';
    if (window.location.host === 'highschool.zoni.edu') {
      endpoint = 'https://www.zoni.edu' + endpoint;
    }
    const res = await fetch(endpoint);
    const { message } = await res.json();
    if (!message) return;
    if (message.message === "Unknown") return;

    // 1) City & ZIP (plain inputs)
    const cityEl = document.getElementById('city');
    const zipEl  = document.getElementById('zip_code');
    if (cityEl) cityEl.value = message.city_name || '';
    if (zipEl)  zipEl.value  = message.postal_code || '';

    // 2) Country (Select2)
    //    we assume country <select> has id="country"
    if (window.$ && $('#country').length) {
      $('#country')
        .val(message.country_iso_code)      // e.g. "US"
        .trigger('change.select2');         // notify Select2
    } else {
      const c = document.getElementById('country');
      if (c) c.value = message.country_iso_code;
    }

    // 3) State (Select2)
    //    make sure States.STATES is already loaded when you call this
    if (message.country_iso_code === 'US') {
      if (window.$ && $('#state').length) {
        $('#state')
          .val(message.subdivision_1_iso_code) // e.g. "NJ"
          .trigger('change.select2');
      } else {
        const s = document.getElementById('state');
        if (s) s.value = message.subdivision_1_iso_code;
      }
    } else {
      if (window.$ && $('#state').length) {
        $('#state')
          .val('00')
          .trigger('change.select2');
      } else {
        const s = document.getElementById('state');
        if (s) s.value = '00';
      }
    }
  } catch (err) {
    console.error('Could not prefill address from IP:', err);
  }
}


function initPasswordStep() {
  const pw = document.getElementById('new-password');
  const pw2 = document.getElementById('confirm-new-password');
  const toggle1 = document.getElementById('new-password-toggle');
  const toggle2 = document.getElementById('confirm-new-password-toggle');

  // Scope the "Continue" button inside the password step (works in both HTMLs)
  const stepNum = [...document.querySelectorAll('[class^="step step-"]')]
    .map(el => el.className.match(/step-(\d+)/)?.[1])
    .filter(Boolean)
    .map(Number)
    .find(n => document.querySelector(`.step-${n} #new-password`));
  const pwStepEl  = stepNum ? document.querySelector(`.step-${stepNum}`) : null;
  const nextBtn   = pwStepEl ? pwStepEl.querySelector('.next-step') : null;

  // Backend feedback container + per-rule icons
  const backendFeedbackElement = document.getElementById('set-password-backend-feedback');
  const ruleIds = {
    len:  'new-8char',
    l:    'new-lcase',
    u:    'new-ucase',
    n:    'new-num',
    s:    'new-symbol',
    eq:   'new-pwmatch'
  };

  if (!pw || !pw2 || !backendFeedbackElement || !nextBtn || !toggle1 || !toggle2) return;

  // toggle visibility buttons
  const toggleVisibility = (input, btn) => {
    const type = input.getAttribute('type') === 'password' ? 'text' : 'password';
    input.setAttribute('type', type);
    btn.innerHTML = type === 'password'
      ? '<i class="fas fa-eye-slash" aria-hidden="true"></i>'
      : '<i class="fas fa-eye" aria-hidden="true"></i>';
  };
  toggle1.addEventListener('click', () => toggleVisibility(pw, toggle1));
  toggle2.addEventListener('click', () => toggleVisibility(pw2, toggle2));

  // icon color/shape update
  const setIcon = (id, ok) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.toggle('fa-check-circle', ok);
    el.classList.toggle('fa-times-circle', !ok);
    el.style.color = ok ? '#78981fFF' : '#ef3a437f';
  };

  // debounce helper
  let debounceTimer;
  const debounce = (fn, ms = 300) => (...args) => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => fn(...args), ms);
  };

  // local checks (fast)
  const runLocalChecks = () => {
    const v1 = pw.value || '';
    const v2 = pw2.value || '';
    const hasLen = v1.length >= 8 && v1.length <= 64;
    const hasL   = /[a-z]/.test(v1);
    const hasU   = /[A-Z]/.test(v1);
    const hasN   = /[0-9]/.test(v1);
    const hasS   = /[^\w\s]/.test(v1); // symbol
    const match  = v1 === v2 && v2.length > 0;

    setIcon(ruleIds.len, hasLen);
    setIcon(ruleIds.l,   hasL);
    setIcon(ruleIds.u,   hasU);
    setIcon(ruleIds.n,   hasN);
    setIcon(ruleIds.s,   hasS);
    setIcon(ruleIds.eq,  match);

    passwordState.meetsLocal = hasLen && hasL && hasU && hasN && hasS && match;
  };

  // backend check (only if local checks pass)
  const validatePasswordBackend = async (password) => {
    const endpointUrl = '/api/method/zoni_edu.zoni_edu.doctype.account_activation.account_activation.validate_password_strength';
    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Frappe-CSRF-Token': frappe.csrf_token
        },
        body: JSON.stringify({ password })
      });
      return await response.json();
    } catch (err) {
      console.error('Error validating password:', err);
      return { message: { meets_requirements: false, feedback: {} } };
    }
  };

  const updateBackendFeedback = (message) => {
    backendFeedbackElement.innerHTML = '';
    if (!message || !message.feedback) return;
    const { warning, suggestions } = message.feedback;
    if (warning) backendFeedbackElement.innerHTML += `<small>${warning}</small><br>`;
    if (Array.isArray(suggestions)) {
      suggestions.forEach(s => backendFeedbackElement.innerHTML += `<small>${s}</small><br>`);
    }
  };

  const runAllChecks = async () => {
    runLocalChecks();

    // backend only after locals pass & value changed
    const v1 = pw.value || '';
    if (!passwordState.meetsLocal) {
      passwordState.meetsBackend = false;
      setIcon('new-backend', false);
      updateBackendFeedback({ feedback: {} });
      return;
    }

    passwordState.backendInFlight = true;
    const result = await validatePasswordBackend(v1);
    passwordState.backendInFlight = false;
    passwordState.lastBackendValue = v1;

    const ok = !!result?.message?.meets_requirements;
    passwordState.meetsBackend = ok;
    setIcon('new-backend', ok);                // ← update the icon here
    updateBackendFeedback(result?.message || { feedback: {} });
  };

  const runAllChecksDebounced = debounce(runAllChecks, 250);

  pw.addEventListener('input', runAllChecksDebounced);
  pw2.addEventListener('input', runAllChecksDebounced);

  // initial paint
  runAllChecks();
}
